import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { SearchInput } from "./search-input";

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  tooltip?: string;
}

interface SearchableMultiSelectProps {
  options: SelectOption[];
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  label?: string;
  placeholder?: string;
  className?: string;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  maxHeight?: number;
  forceOpen?: boolean;
  loading?: boolean;
  singleSelect?: boolean;
  optionTruncate?: boolean;
  optionTruncateWidth?: number;
}

export function SearchableMultiSelect({
  options,
  selectedValues,
  onChange,
  label,
  searchTerm,
  loading = false,
  setSearchTerm,
  placeholder = "Select options...",
  className = "",
  maxHeight = 200,
  forceOpen = false,
  singleSelect = false,
  optionTruncate = false,
  optionTruncateWidth = 250,
}: SearchableMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Use internal state or forced state from parent
  const dropdownOpen = forceOpen || isOpen;

  // Prevent closing when input is clicked
  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleSelect = (optionValue: string) => {
    if (singleSelect) {
      // In single select mode, only allow one value and close dropdown
      if (selectedValues.includes(optionValue)) {
        onChange([]);
      } else {
        onChange([optionValue]);
      }
      setIsOpen(false);
    } else {
      // In multi-select mode, toggle the value
      if (selectedValues.includes(optionValue)) {
        onChange(selectedValues.filter((value) => value !== optionValue));
      } else {
        onChange([...selectedValues, optionValue]);
      }
    }
  };

  const handleRemove = (e: React.MouseEvent, optionValue: string) => {
    e.stopPropagation();
    onChange(selectedValues.filter((value) => value !== optionValue));
  };

  const renderSelectedValues = () => {
    if (selectedValues.length === 0) {
      return (
        <span className="text-sm text-muted-foreground">{placeholder}</span>
      );
    }

    if (singleSelect) {
      const option = options.find((opt) => opt.value === selectedValues[0]);
      return <span className="text-sm">{option?.label}</span>;
    }
    return (
      <div className="flex flex-wrap items-center gap-1 max-h-[100px] overflow-y-auto">
        {selectedValues.map((value) => {
          const option = options.find((opt) => opt.value === value);
          return (
            <Badge
              key={value}
              variant="secondary"
              className="flex items-center gap-1 px-1.5 font-normal"
            >
              {option?.label || value}
              {/* <X
                  className="h-3 w-3 cursor-pointer text-muted-foreground hover:text-foreground"
                  onClick={(e) => handleRemove(e, value)}
                /> */}
            </Badge>
          );
        })}
      </div>
    );
  };

  return (
    <div className={cn("space-y-1 w-full", className)}>
      {label && <Label>{label}</Label>}
      <DropdownMenu open={dropdownOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <div className="flex items-center justify-between gap-1 min-h-8 py-1 px-2 border rounded-md cursor-pointer hover:border-primary/50 w-full">
            {renderSelectedValues()}
            <ChevronDown className="h-4 w-4 ml-auto text-muted-foreground shrink-0" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="min-w-[var(--radix-dropdown-menu-trigger-width)] p-0"
          align="start"
          sideOffset={4}
        >
          <div className="p-2 border-b">
            {/* <div className="flex items-center gap-2 px-2 py-1 rounded-md border">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search..."
                className="flex-1 border-0 outline-none bg-transparent text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={handleInputClick}
                onFocus={(e) => e.stopPropagation()}
              />
              {searchTerm && (
                <X
                  className="h-3.5 w-3.5 text-muted-foreground cursor-pointer hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchTerm("");
                  }}
                />
              )}
            </div> */}
            <SearchInput
              onSearch={(value) => setSearchTerm(value)}
              placeholder="Search..."
              className="max-w-none sm:w-full"
              delay={500}
            />
          </div>
          <ScrollArea style={{ height: `${maxHeight}px` }}>
            <div className="p-2 space-y-1">
              {loading ? (
                <div className="flex items-center justify-center p-4">
                  <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mr-2"></div>
                  <span className="text-sm text-muted-foreground">
                    Loading...
                  </span>
                </div>
              ) : options?.length === 0 ? (
                <div className="p-2 text-sm text-muted-foreground text-center">
                  No results found
                </div>
              ) : (
                options?.map((option, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2 p-2 hover:bg-muted rounded-md cursor-pointer"
                    onClick={() => handleSelect(option.value)}
                    title={optionTruncate ? option.label : undefined}
                  >
                    {singleSelect ? (
                      <Check
                        className={cn(
                          "h-4 w-4",
                          selectedValues.includes(option.value)
                            ? "visible"
                            : "invisible"
                        )}
                      />
                    ) : (
                      <Checkbox
                        checked={selectedValues.includes(option.value)}
                        onCheckedChange={() => {}}
                        className="border-gray-300 [data-state=checked]:border-accent"
                      />
                    )}

                    <Label
                      className={cn(
                        "flex-grow cursor-pointer font-normal flex justify-between items-center",
                        optionTruncate && `truncate`
                      )}
                      style={{
                        maxWidth: optionTruncate
                          ? `${optionTruncateWidth}px`
                          : "100%",
                      }}
                    >
                      <span>{option.label}</span>
                      {option.icon && (
                        <span title={option.tooltip}>{option.icon}</span>
                      )}
                    </Label>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
